var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import MPStorage from "@/services/LocalStorage";
import { customKeys } from "@/services/LocalStorage/LocalStorage";
import { MixpanelEvents } from "@/utilities/mixpanel/events";
/**
 * The statuses that mean a partner has finished onboarding.
 *
 * `initial_onboarding_send_and_receive_messages` is in here because it is patched when the
 * partner reaches the end of the LAST REQUIRED milestone. Note what that does and does not
 * mean: the final substep shows a curl snippet and its Continue button is unconditional
 * (isNextButtonDisabled returns false there), so this signals "worked through the required
 * steps", NOT "sent a message". A verified-delivery condition would be a separate change.
 * The milestone
 * after it, Register as Tech Provider, carries `optional: true` in the flow's own milestone
 * list, and completion used to be reported only from its button. That is why the funnel
 * showed 1,585 partners starting against 88 completing: partners who did all the required
 * work and then navigated away were never counted.
 *
 * `active` stays a completion status so the optional step still reports for partners who do
 * press it, and so any future path that activates a partner is covered. Whichever transition
 * happens first wins — reportOnboardingCompletedOnce drops the second.
 */
var COMPLETION_STATUSES = [
    "initial_onboarding_send_and_receive_messages",
    "active"
];
/**
 * Onboarding ends when a partner who was mid-flow reaches one of the completion statuses.
 * A move between earlier steps is not a completion, and a partner who was already active
 * did not just finish anything — an admin re-activating one must not look like a new
 * completion.
 */
export var isOnboardingCompletion = function (previousStatus, nextStatus) {
    // A patch to the status the partner already holds is not a completion. This matters for
    // the partners already parked on initial_onboarding_send_and_receive_messages: the
    // Send & Receive milestone stays clickable for them, so walking it again re-patches the
    // same status, and without this guard every revisit would report a fresh completion
    // months after the fact. For `active` the guard was previously incidental -- "active"
    // does not contain "initial_" -- and incidental guards are the ones that get widened
    // away, as this one just was.
    return previousStatus !== nextStatus &&
        COMPLETION_STATUSES.includes(nextStatus) &&
        Boolean(previousStatus === null || previousStatus === void 0 ? void 0 : previousStatus.includes("initial_"));
};
/**
 * Reports partner_onboarding_completed at most once per partner.
 *
 * The ids live in localStorage rather than sessionStorage: a partner completes onboarding
 * once, so a second report from a new tab is a duplicate, not a new completion. Stored as a
 * comma-separated list and compared per entry, so one id cannot mask another that merely
 * contains it (PARTNER1 vs PARTNER10).
 */
export var reportOnboardingCompletedOnce = function (partnerId) {
    var _a;
    var reported = MPStorage.getFromLocalStorage(customKeys.ONBOARDING_COMPLETED_REPORTED_FOR);
    if (((_a = reported === null || reported === void 0 ? void 0 : reported.split(",")) !== null && _a !== void 0 ? _a : []).includes(partnerId))
        return;
    MPStorage.saveToLocalStorage(customKeys.ONBOARDING_COMPLETED_REPORTED_FOR, [reported, partnerId].filter(Boolean).join(","));
    MixpanelEvents.partnerOnboardingCompleted({
        source: "activation_checklist",
        partnerId: partnerId
    });
};
/**
 * Performs a partner status change and reports the completion, in that order.
 *
 * The ordering is the contract, and it is why this is a function rather than three lines
 * inside the component. The event used to be sent beside an unawaited PATCH whose errors
 * the caller swallows, so a completion that failed server-side still reported success. Here
 * the report is unreachable unless `patch` resolves, and `previousStatus` is a parameter, so
 * it cannot accidentally be read from the store after the response has overwritten it.
 */
export function applyPartnerStatusChange(params) {
    return __awaiter(this, void 0, Promise, function () {
        var partnerId, previousStatus, nextStatus, patch, onPatched, updated;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    partnerId = params.partnerId, previousStatus = params.previousStatus, nextStatus = params.nextStatus, patch = params.patch, onPatched = params.onPatched;
                    return [4 /*yield*/, patch()];
                case 1:
                    updated = _a.sent();
                    onPatched(updated);
                    if (isOnboardingCompletion(previousStatus, nextStatus)) {
                        reportOnboardingCompletedOnce(partnerId);
                    }
                    return [2 /*return*/];
            }
        });
    });
}
//# sourceMappingURL=data:application/json;base64,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